import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Observable, throwError } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';
import { Cliente } from '../../../models/cliente';
import { DataCombo, DataComboServicio } from '../../../models/combo';
import { Evento, RegisterEvent, EventServicio, EventProducto, RegisterItemCantidad } from '../../../models/evento';
import { ClienteService } from '../../../services/cliente.service';
import { ProductoService } from '../../../services/producto.service';
import { ServicioService } from '../../../services/servicio.service';
import { UtilsService } from '../../../services/utils.service';

@Component({
  selector: 'app-form-evento',
  templateUrl: './form-evento.component.html',
  styleUrls: ['./form-evento.component.scss']
})
export class FormEventoComponent implements OnInit {

  private readonly CLIENTE_SIN_CITA = "Cliente para compras sin cita";

  @Input() esSoloCrearCita: boolean = true;
  @Input() cita: Evento | null = null;
  @Input() clienteId: number;

  @Output() sendToastMsg: EventEmitter<object> = new EventEmitter<object>();
  @Output() cancel = new EventEmitter<void>();
  @Output() save = new EventEmitter<any>(); // Evento para enviar datos

  public clientesCombo: DataCombo[];
  public serviciosCombo: DataComboServicio[];
  public productosCombo: DataCombo[];

  public eForm: FormGroup;
  public tiendaId: number;

  // modal cliente
  public modalConfig = [];
  public modalTitle;
  public modalData: any = {};
  public isModalCreacionOpen = false;
  // controles para el select, no se resetea si se selcciona el mismo producto despues de borrarlo
  public productoSelectControl = new FormControl(null);
  public servicioSelectControl = new FormControl(null);

  //fecha/hora
  horas: number[] = Array.from({ length: 24 }, (_, i) => i); // 0–23
  minutos: number[] = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55];

  constructor(
    private clienteService: ClienteService,
    private servicioService: ServicioService,
    private productoService: ProductoService,
    private utilsService: UtilsService,
    private fb: FormBuilder
  ) { }

  ngOnInit(): void {
    this.tiendaId = this.utilsService.getCurrentTiendaId();
    this.prepararForm();
  }

  async prepararForm() {
    await Promise.all([
      this.getCLientesCombo().toPromise(),
      this.getServiciosCombo().toPromise(),
      this.getProductosCombo().toPromise()
    ]);

    this.buildForm(this.cita);
    this.suscribirseACambios();

    if (!this.cita) {
      this.setFechaPorDefecto();
    } else {
      this.setFechasDesdeCita(this.cita);
    }
  }

  private suscribirseACambios() {
    this.eForm?.get('horaDesde')?.valueChanges.subscribe(() => this.calcularHoraFin());
    this.eForm?.get('minutosDesde')?.valueChanges.subscribe(() => this.calcularHoraFin());
    this.eForm?.get('dia')?.valueChanges.subscribe(() => this.actualizarFechas());
    this.eForm?.get('horaDesde')?.valueChanges.subscribe(() => this.actualizarFechas());
    this.eForm?.get('minutosDesde')?.valueChanges.subscribe(() => this.actualizarFechas());
    this.eForm.get('servicios').valueChanges.subscribe(() => {
      this.actualizarFechas();
    });
    this.eForm.get('productos').valueChanges.subscribe(() => {
      this.actualizarFechas();
    });
  }

  private buildForm(cita?: Evento): void {
    // Convertir clienteId a número para compatibilidad con el combo
    const clienteIdValue = cita?.clienteId ?? (this.clienteId ? Number(this.clienteId) : null);

    this.eForm = this.fb.group({
      id: [cita?.id ?? null],
      clienteId: [clienteIdValue, Validators.required],
      tiendaId: [cita?.tiendaId ?? this.tiendaId],
      ticketId: [cita?.ticketId ?? null],
      description: [cita?.description ?? null],
      startsAt: [cita?.startsAt ?? null, Validators.required],
      endsAt: [cita?.endsAt ?? null, Validators.required],
      servicios: this.fb.array(this.getEventServicioFormGroups(cita?.eventServicios ?? [])),
      productos: this.fb.array(this.getEventProductoFormGroups(cita?.eventProductos ?? [])),
      isBloqueaHoras: [cita?.isBloqueaHoras ?? false],
      // campos a eliminar antes de enviar
      dia: [null, Validators.required],
      horaDesde: [null, Validators.required],
      minutosDesde: [null, Validators.required],
      horaHasta: [{ value: null, disabled: true }],
      minutosHasta: [{ value: null, disabled: true }]
    });
  }

  guardarCita() {
    const cita = this.construirCita();
    if (!cita) {
      console.warn('No se pudo construir la cita');
      return;
    }
    this.save.emit(cita);
  }

  onCancel(): void {
    this.cancel.emit();
  }

  construirCita(): RegisterEvent {
    const fechas = this.getFechasParaBackend();
    if (!fechas) {
      console.warn('Faltan datos para construir las fechas');
      return;
    }

    const payload = {
      ...this.eForm.getRawValue(),
      ...fechas,
      servicios: this.getPayloadServicios(),
      productos: this.getPayloadProductos()
    };

    // Eliminar campos auxiliares
    delete payload.dia;
    delete payload.horaDesde;
    delete payload.minutosDesde;
    delete payload.horaHasta;
    delete payload.minutosHasta;

    return payload as RegisterEvent;
  }

  // datos iniciales en edicion
  private getEventServicioFormGroups(servicios: EventServicio[]): FormGroup[] {
    return servicios.map(s => {
      const servicioCombo = this.serviciosCombo.find(sc => sc.value === s.servicio?.id);

      if (!servicioCombo) {
        console.warn(`No se encontró el servicio ${s.servicio?.id} en el combo`);
      }

      return this.fb.group({
        value: [servicioCombo?.value ?? s.servicio?.id],
        nombre: [servicioCombo?.nombre ?? s.servicio?.baseServicio?.name],
        detalles: [servicioCombo?.detalles ?? null],
        codigoTipoServicio: [servicioCombo?.codigoTipoServicio ?? s.servicio?.baseServicio?.servicioTipo?.codigoTipo],
        color: [servicioCombo?.color ?? s.servicio?.baseServicio?.servicioTipo?.color],
        cantidad: [s.cantidad ?? 1, [Validators.required, Validators.min(1)]]
      });
    });
  }

  private getEventProductoFormGroups(productos: EventProducto[]): FormGroup[] {
    return productos.map(p => {
      const productoCombo = this.productosCombo.find(pc => pc.value === p.producto?.id);

      return this.fb.group({
        value: [productoCombo?.value ?? p.producto?.id],
        label: [productoCombo?.label ?? p.producto?.baseProducto?.name ?? ''],
        cantidad: [p.cantidad ?? 1, [Validators.required, Validators.min(1)]]
      });
    });
  }

  private setFechasDesdeCita(cita: Evento): void {
    if (!cita?.startsAt || !cita?.endsAt) return;

    const inicio = new Date(cita.startsAt);
    const fin = new Date(cita.endsAt);

    this.eForm.patchValue({
      dia: this.utilsService.formatearFechaISOCalendario(inicio),
      horaDesde: inicio.getHours(),
      minutosDesde: inicio.getMinutes(),
      horaHasta: fin.getHours(),
      minutosHasta: fin.getMinutes()
    });
  }


  // manejo fechas
  // fecha inicial, actual con hora redondeada
  private setFechaPorDefecto(): void {
    if (!this.eForm.get('dia')?.value) {
      const ahora = new Date();
      ahora.setMinutes(0, 0, 0);
      ahora.setHours(ahora.getHours() + 1);

      this.eForm.patchValue({
        dia: this.utilsService.formatearFechaISOCalendario(ahora),
        horaDesde: ahora.getHours(),
        minutosDesde: ahora.getMinutes(),
        horaHasta: ahora.getHours(),
        minutosHasta: ahora.getMinutes()
      });
      this.actualizarFechas();
    }
  }
  // hora inicio + duracion servicios
  private calcularHoraFin(): void {
    const horaDesde = this.eForm.get('horaDesde')?.value;
    const minutosDesde = this.eForm.get('minutosDesde')?.value;
    const duracionServicios = this.obtenerDuracionTotalServicios();

    if (horaDesde == null || minutosDesde == null || duracionServicios === 0) return;

    const inicio = new Date();
    inicio.setHours(horaDesde, minutosDesde, 0, 0);

    const fin = new Date(inicio.getTime() + duracionServicios * 60000); // sumar duración en ms

    this.eForm.patchValue({
      horaHasta: fin.getHours(),
      minutosHasta: fin.getMinutes()
    });
  }

  actualizarFechas(): void {
    const { dia, horaDesde, minutosDesde } = this.eForm.value;
    const duracionMinutos = this.obtenerDuracionTotalServicios();

    if (!dia || horaDesde == null || minutosDesde == null || duracionMinutos == null) return;

    const startsAt = new Date(dia);
    startsAt.setHours(horaDesde);
    startsAt.setMinutes(minutosDesde);
    startsAt.setSeconds(0);
    startsAt.setMilliseconds(0);

    const endsAt = new Date(startsAt);
    endsAt.setMinutes(endsAt.getMinutes() + duracionMinutos);

    this.eForm.patchValue({
      startsAt: this.utilsService.formatearFechaISOStr(startsAt),
      endsAt: this.utilsService.formatearFechaISOStr(endsAt),
      horaHasta: endsAt.getHours(),
      minutosHasta: endsAt.getMinutes()
    });
  }

  private obtenerDuracionTotalServicios(): number {
    const servicios = this.eForm.get('servicios')?.value;
    if (!servicios || servicios.length === 0) return 0;

    let duracionTotal = 0;

    for (const servicio of servicios) {
      const detalles = servicio?.detalles;
      if (!detalles) continue;

      const match = detalles.match(/(\d+)'/); // busca número seguido de apóstrofe
      if (match) {
        duracionTotal += parseInt(match[1], 10) * servicio.cantidad;
      }
    }

    return duracionTotal;
  }
  // prepara fechas para enviar a back
  getFechasParaBackend(): { startsAt: string; endsAt: string } | null {
    const diaStr = this.eForm.get('dia')?.value;
    const horaDesde = this.eForm.get('horaDesde')?.value;
    const minutosDesde = this.eForm.get('minutosDesde')?.value;
    const duracionMinutos = this.obtenerDuracionTotalServicios();

    if (!diaStr || horaDesde == null || minutosDesde == null) {
      return null; // Evita errores si falta algún dato
    }

    const [year, month, day] = diaStr.split('-').map(Number);

    const localDateTimeDesde = new Date(year, month - 1, day, horaDesde, minutosDesde, 0, 0);
    //const duracionMinutos = this.obtenerDuracionTotalServicios();
    const localDateTimeHasta = new Date(localDateTimeDesde.getTime() + duracionMinutos * 60000);

    return {
      startsAt: this.utilsService.formatearFechaISOStr(localDateTimeDesde),
      endsAt: this.utilsService.formatearFechaISOStr(localDateTimeHasta)
    };
  }

  // seleccion de cliente o creacion
  openCreacionClienteModal(): void {
    this.modalConfig = this.clienteService.buildForm();
    this.modalTitle = 'titulos.clientes.editar';
    this.isModalCreacionOpen = true;
  }

  closeCreacionClienteModal(): void {
    this.isModalCreacionOpen = false;
  }

  saveCliente(data: Cliente): void {
    data.activo = true;
    data.tiendaId = this.utilsService.getCurrentTiendaId();
    this.clienteService.save(data).subscribe(
      (data: Cliente) => {
        // this.sendToastMsg.emit({ mensajeError: false, mensaje: 'Cliente creado correctamente' });
        this.closeCreacionClienteModal();
        this.getCLientesCombo().subscribe(() => {
          this.clienteId = data.id;
          this.eForm.get('clienteId').setValue(data.id);
        });
      },
      (error) => {
        var errorMessage = <any>error;
        if (errorMessage != null) {
          this.sendToastMsg.emit({
            mensajeError: true,
            mensaje: ('Error al crear el cliente: ' + error.error.message)
          });
        }
      }
    );
  }

  onClienteChange(cliente: DataCombo): void {
    this.clienteId = cliente.value;
  }

  // lista de servicios para la cita
  get serviciosFormArray(): FormArray {
    return this.eForm.get('servicios') as FormArray;
  }

  onServicioSeleccionado(servicio: DataComboServicio) {
    this.servicioSelectControl.reset();
    const yaExiste = this.serviciosFormArray.controls.find(ctrl => ctrl.get('value')?.value === servicio.value);
    if (!yaExiste) {
      const grupo = this.fb.group({
        value: [servicio.value],
        nombre: [servicio.nombre],
        detalles: [servicio.detalles],
        codigoTipoServicio: [servicio.codigoTipoServicio],
        color: [servicio.color],
        cantidad: [1, [Validators.required, Validators.min(1)]]
      });

      this.serviciosFormArray.push(grupo);
    }
  }

  eliminarServicio(index: number) {
    this.serviciosFormArray.removeAt(index);
  }

  getPayloadServicios(): RegisterItemCantidad[] {
    return this.serviciosFormArray.value.map(({ value, cantidad }) => ({ id: value, cantidad }));
  }

  // lista de productos para la cita
  get productosFormArray(): FormArray {
    return this.eForm.get('productos') as FormArray;
  }

  onProductoSeleccionado(producto: DataCombo) {
    this.productoSelectControl.reset();
    const yaExiste = this.productosFormArray.controls.find(ctrl => ctrl.get('value')?.value === producto.value);
    if (!yaExiste) {
      const grupo = this.fb.group({
        value: [producto.value],
        label: [producto.label],
        cantidad: [1, [Validators.required, Validators.min(1)]]
      });

      this.productosFormArray.push(grupo);
      this.actualizarFechas();
    }
  }

  eliminarProducto(index: number) {
    this.productosFormArray.removeAt(index);
  }

  getPayloadProductos(): RegisterItemCantidad[] {
    return this.productosFormArray.value.map(({ value, cantidad }) => ({ id: value, cantidad }));
  }

  // combos
  private getProductosCombo(): Observable<DataCombo[]> {
    return this.productoService.getProductosCombo(this.tiendaId).pipe(
      tap(data => {
        this.productosCombo = data;
      }),
      catchError(error => {
        var errorMessage = <any>error;
        if (errorMessage != null) {
          this.sendToastMsg.emit({
            mensajeError: true,
            mensaje: ('Se produjo un error al obtener los productos: ' + error.error.message)
          });
        }
        return throwError(error);
      })
    );
  }

  private getServiciosCombo(): Observable<DataComboServicio[]> {
    return this.servicioService.getServiciosCombo(this.tiendaId).pipe(
      tap(data => {
        this.serviciosCombo = data;
      }),
      catchError(error => {
        var errorMessage = <any>error;
        if (errorMessage != null) {
          this.sendToastMsg.emit({
            mensajeError: true,
            mensaje: ('Se produjo un error al obtener los servicios: ' + error.error.message)
          });
        }
        return throwError(error);
      })
    );
  }

  private getCLientesCombo(): Observable<DataCombo[]> {
    return this.clienteService.getClientesCombo(this.tiendaId).pipe(
      tap(data => {
        this.clientesCombo = data;
        if (!this.cita && !this.clienteId) {
          this.clienteId = this.clientesCombo.find(c =>
            c.label && c.label.startsWith(this.CLIENTE_SIN_CITA)
          ).value;
        }

      }),
      catchError(error => {
        var errorMessage = <any>error;
        if (errorMessage != null) {
          this.sendToastMsg.emit({
            mensajeError: true,
            mensaje: ('Se produjo un error al obtener los clientes: ' + error.error.message)
          });
        }
        return throwError(error);
      })
    );
  }

  updateToastMsg($event: any) {
    this.sendToastMsg.emit({
      mensajeError: $event.mensajeError,
      mensaje: $event.mensaje,
    });
  }
}
